<template>
  <div class="space-y-3">
    <!-- Toolbar: search + filters -->
    <div
      v-if="searchable || filterGroups.length || $slots['toolbar-actions']"
      class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex flex-1 items-center gap-2">
        <!-- max-w-xs truncated longer placeholders (e.g. "Search by cashback id,
             transaction id, or user id", which needs ~319px of text space); max-w-md fits
             them with headroom. It is a cap, so shorter toolbars still shrink to fit. -->
        <div v-if="searchable" class="relative flex-1 sm:max-w-md">
          <Search class="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground z-10" />
          <Input
            :model-value="search"
            type="text"
            :placeholder="searchPlaceholder"
            class="h-8 pl-8 pr-8"
            @update:model-value="onSearchInput"
            @keyup.enter="emitSearchNow"
          />
          <button
            v-if="search"
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            @click="onClearSearch"
          >
            <X class="size-4" />
          </button>
        </div>
      </div>

      <div v-if="filterGroups.length || $slots['toolbar-actions']" class="flex flex-wrap items-center gap-2">
        <slot name="toolbar-actions" />
        <Popover v-if="filterGroups.length" v-model:open="filterOpen">
          <PopoverTrigger as-child>
            <Button type="button" variant="outline" size="sm" class="h-8 gap-1.5">
              <Filter class="size-4" />
              Filters
              <span
                v-if="activeFilterCount"
                class="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground"
              >
                {{ activeFilterCount }}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" class="w-64 p-0">
            <div class="flex items-center justify-between px-3 pt-3">
              <span class="text-sm font-medium">Filters</span>
              <button
                v-if="activeFilterCount"
                type="button"
                class="text-xs font-medium text-primary hover:underline"
                @click="clearFilters"
              >
                Clear all
              </button>
            </div>
            <Separator />
            <div class="max-h-80 overflow-y-auto px-3 pb-3">
              <div v-for="(group, gi) in filterGroups" :key="group.key" :class="gi > 0 ? 'mt-4' : ''">
                <p class="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {{ group.label }}
                </p>
                <div class="grid gap-1.5">
                  <label
                    v-for="opt in group.options"
                    :key="String(opt.value)"
                    class="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <Checkbox
                      :model-value="isFilterSelected(group.key, opt.value)"
                      @update:model-value="(v) => toggleFilter(group.key, opt.value, v)"
                    />
                    {{ opt.label }}
                  </label>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>

    <!-- Sits between the toolbar and the table, for filters that belong under the search
         rather than above it (e.g. a country or segment strip). -->
    <div v-if="$slots['toolbar-below']" class="mb-3">
      <slot name="toolbar-below" />
    </div>

    <div class="rounded-md border border-border overflow-hidden">
      <div class="overflow-x-auto">
        <Table>
          <TableHeader class="bg-muted/40">
            <TableRow class="hover:bg-transparent">
              <TableHead
                v-for="header in headers"
                :key="header.value"
                :class="[
                  isRightAligned(header) ? 'text-right' : '',
                  dense ? 'h-9 px-2.5' : 'h-11 px-3',
                  'text-xs uppercase tracking-wide text-muted-foreground',
                ]"
              >
                <slot :name="`header-${header.value}`" :text="header.text" :header="header">
                  {{ header.text }}
                </slot>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <template v-if="loading">
              <TableRow>
                <TableCell :colspan="headers.length" class="h-32">
                  <slot name="loading">
                    <div class="flex justify-center py-6">
                      <Loader2 class="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  </slot>
                </TableCell>
              </TableRow>
            </template>
            <template v-else-if="error">
              <TableRow>
                <TableCell :colspan="headers.length" class="h-32 text-center">
                  <slot name="error">
                    <div class="flex flex-col items-center justify-center gap-2 py-6 text-sm">
                      <TriangleAlert class="w-5 h-5 text-destructive" />
                      <span class="text-muted-foreground">{{ errorMessage }}</span>
                      <Button v-if="retryable" size="sm" variant="outline" @click="$emit('retry')">
                        Retry
                      </Button>
                    </div>
                  </slot>
                </TableCell>
              </TableRow>
            </template>
            <template v-else-if="!displayItems.length">
              <TableRow>
                <TableCell :colspan="headers.length" class="h-32 text-center text-muted-foreground">
                  <slot name="empty-message">
                    <div class="flex justify-center py-4">
                      <div class="flex items-center gap-2 rounded-full bg-gray-50 text-gray-500 dark:bg-muted dark:text-muted-foreground px-5 py-2">
                        <span class="text-sm">No results</span>
                        <Database class="w-4 h-4" />
                      </div>
                    </div>
                  </slot>
                </TableCell>
              </TableRow>
            </template>
            <template v-else>
              <TableRow
                v-for="(row, rowIdx) in displayItems"
                :key="rowIdx"
                :class="[noHover ? '' : 'hover:bg-muted/50', rowClickable ? 'cursor-pointer' : '']"
                @click="onRowClick(row, rowIdx, $event)"
              >
                <TableCell
                  v-for="header in headers"
                  :key="header.value"
                  :class="[
                    isRightAligned(header) ? 'text-right' : '',
                    dense ? 'px-2.5 py-1.5' : 'px-3 py-3',
                    'align-middle',
                  ]"
                >
                  <slot
                    :name="`item-${header.value}`"
                    v-bind="row"
                    :row="row"
                    :index="(page - 1) * rowsPerPage + rowIdx"
                  >
                    {{ resolveValue(row, header.value) }}
                  </slot>
                </TableCell>
              </TableRow>
            </template>
          </TableBody>
        </Table>
      </div>
    </div>

    <!-- Pagination footer -->
    <div
      v-if="!hideFooter"
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-muted-foreground"
    >
      <div class="flex items-center gap-2">
        <span class="text-xs">Rows per page</span>
        <Select
          :model-value="String(rowsPerPage)"
          @update:model-value="onRowsPerPageChange"
        >
          <SelectTrigger size="sm" class="h-8 w-18">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="opt in rowsItemsActual" :key="opt" :value="String(opt)">
              {{ opt }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex items-center gap-3">
        <span class="text-xs tabular-nums">
          {{ rangeStart }}–{{ rangeEnd }} of {{ totalItems }}
        </span>
        <div class="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="h-8 w-8"
            :disabled="page <= 1"
            @click="goToPage(1)"
            aria-label="First page"
          >
            <ChevronsLeft class="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="h-8 w-8"
            :disabled="page <= 1"
            @click="goToPage(page - 1)"
            aria-label="Previous page"
          >
            <ChevronLeft class="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="h-8 w-8"
            :disabled="page >= totalPages"
            @click="goToPage(page + 1)"
            aria-label="Next page"
          >
            <ChevronRight class="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="h-8 w-8"
            :disabled="page >= totalPages"
            @click="goToPage(totalPages)"
            aria-label="Last page"
          >
            <ChevronsRight class="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../table/index.js'
import { Button } from '../button/index.js'
import { Popover, PopoverTrigger, PopoverContent } from '../popover/index.js'
import { Checkbox } from '../checkbox/index.js'
import { Separator } from '../separator/index.js'
import { Input } from '../input/index.js'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../select/index.js'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Database,
  Search,
  Filter,
  X,
  TriangleAlert,
} from 'lucide-vue-next'

export default {
  name: 'DataTable',
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Checkbox,
    Separator,
    Input,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2,
    Database,
    Search,
    Filter,
    X,
    TriangleAlert,
  },
  props: {
    headers: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: [Boolean, String], default: false },
    hideFooter: { type: Boolean, default: false },
    noHover: { type: Boolean, default: false },
    alternating: { type: Boolean, default: false },
    dense: { type: Boolean, default: true },
    serverOptions: { type: Object, default: null },
    serverItemsLength: { type: Number, default: undefined },
    rowsItems: { type: Array, default: () => [10, 25, 50, 100] },
    defaultRowsPerPage: { type: Number, default: 25 },
    tableMinHeight: { type: [Boolean, Number], default: false },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Search…' },
    search: { type: String, default: '' },
    searchDebounce: { type: Number, default: 300 },
    filterGroups: { type: Array, default: () => [] },
    filters: { type: Object, default: () => ({}) },
  },
  emits: ['update:server-options', 'update:search', 'search', 'clear-search', 'update:filters', 'filter-change', 'row-click', 'retry'],
  computed: {
    errorMessage() {
      return typeof this.error === 'string' && this.error
        ? this.error
        : "Couldn't load data. Please try again."
    },
    retryable() {
      const vnodeProps = this.$.vnode.props
      return !!(vnodeProps && (vnodeProps.onRetry || vnodeProps['onRetry']))
    },
    isServerSide() {
      return this.serverOptions !== null && this.serverOptions !== undefined
    },
    page() {
      if (this.isServerSide) return this.serverOptions.page || 1
      return this.localPage
    },
    rowsPerPage() {
      if (this.isServerSide) return this.serverOptions.rowsPerPage || 25
      return this.localRowsPerPage
    },
    totalItems() {
      if (this.isServerSide && typeof this.serverItemsLength === 'number') {
        return this.serverItemsLength
      }
      return this.items.length
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.rowsPerPage))
    },
    displayItems() {
      if (this.isServerSide) return this.items
      const start = (this.page - 1) * this.rowsPerPage
      return this.items.slice(start, start + this.rowsPerPage)
    },
    rangeStart() {
      if (this.totalItems === 0) return 0
      return (this.page - 1) * this.rowsPerPage + 1
    },
    rangeEnd() {
      return Math.min(this.page * this.rowsPerPage, this.totalItems)
    },
    rowsItemsActual() {
      const r = [...this.rowsItems]
      if (!r.includes(this.rowsPerPage)) r.push(this.rowsPerPage)
      return r.sort((a, b) => a - b)
    },
    rowClickable() {
      const vnodeProps = this.$.vnode.props
      return !!(vnodeProps && (vnodeProps.onRowClick || vnodeProps['onRow-click']))
    },
    activeFilterCount() {
      return Object.values(this.filters || {}).reduce(
        (acc, vals) => acc + (Array.isArray(vals) ? vals.length : vals ? 1 : 0),
        0,
      )
    },
  },
  data() {
    return {
      localPage: 1,
      localRowsPerPage: this.defaultRowsPerPage,
      filterOpen: false,
      searchTimer: null,
    }
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    resolveValue(row, path) {
      if (!path) return ''
      return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), row)
    },
    onSearchInput(value) {
      this.$emit('update:search', value)
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        // Emptying the field (backspacing to blank) resets the table the same
        // way the clear (X) button does, rather than running a blank search.
        if (value === '') this.$emit('clear-search')
        else this.$emit('search', value)
      }, this.searchDebounce)
    },
    emitSearchNow() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.$emit('search', this.search)
    },
    onRowClick(row, rowIdx, event) {
      if (!this.rowClickable) return
      // Don't navigate when the click came from an interactive control inside the row
      // (a copy button, action button, link, form field). Otherwise e.g. clicking the
      // "Copy ID" button also clicks through to the row's detail page.
      if (event && event.target && event.target.closest) {
        const interactive = event.target.closest(
          'button, a, input, select, textarea, [role="button"]'
        )
        if (interactive && event.currentTarget && event.currentTarget.contains(interactive)) {
          return
        }
      }
      this.$emit('row-click', row, rowIdx)
    },
    isRightAligned(header) {
      if (header.align) return header.align === 'right'
      if (header.fixed === 'right') return true
      // Action columns default to right-aligned without needing per-page config.
      return header.value === 'action' || header.value === 'actions'
    },
    onClearSearch() {
      this.$emit('update:search', '')
      this.$emit('clear-search')
    },
    isFilterSelected(key, value) {
      const vals = this.filters[key]
      return Array.isArray(vals) ? vals.includes(value) : vals === value
    },
    toggleFilter(key, value, checked) {
      const current = Array.isArray(this.filters[key]) ? [...this.filters[key]] : []
      const next = checked
        ? [...current, value]
        : current.filter((v) => v !== value)
      const updated = { ...this.filters, [key]: next }
      this.$emit('update:filters', updated)
      this.$emit('filter-change', updated)
    },
    clearFilters() {
      const cleared = {}
      for (const group of this.filterGroups) cleared[group.key] = []
      this.$emit('update:filters', cleared)
      this.$emit('filter-change', cleared)
    },
    goToPage(p) {
      const next = Math.max(1, Math.min(this.totalPages, p))
      if (this.isServerSide) {
        this.$emit('update:server-options', { ...this.serverOptions, page: next })
      } else {
        this.localPage = next
      }
    },
    onRowsPerPageChange(val) {
      const n = Number(val)
      if (this.isServerSide) {
        this.$emit('update:server-options', {
          ...this.serverOptions,
          page: 1,
          rowsPerPage: n,
        })
      } else {
        this.localRowsPerPage = n
        this.localPage = 1
      }
    },
  },
}
</script>
